import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { XIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router';
import * as yup from 'yup';

import useFaqSubmit from '@/components/atoms/FileUpload/useFaqSubmit';
import MarkdownEditorBlockNote from '@/components/atoms/MarkdownEditor/MarkdownEditorBlockNote';
import {
  SidebarWrapper,
  SidebarWrapperContent,
  SidebarWrapperFooter,
  SidebarWrapperForm,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { UnsavedChangesModal } from '@/components/organisms/Modals/UnsavedChangesModal/UnsavedChangesModal';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { URLInput } from '@/components/ui/custom/URLInput/URLInput';
import {
  Form,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
  FormLabel,
  FormDescription,
} from '@/components/ui/form';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import useDatasources from '@/hooks/useDatasources';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useForceRerender } from '@/hooks/useForceRerender';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { useDocumentContent } from '@/modules/collections/hooks/useDocumentContent';
import { getPermissions } from '@/redux/permissions/selectors';
import { setDatasourceId, setDocumentId } from '@/redux/session/actions';
import { selectAccountId, selectDatasourceId, selectDocumentId } from '@/redux/session/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { DatasourceType } from '@/shared/types/collections';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { COLLECTIONS_WEBPAGE_SOURCE_DOCS_URL, FILES_BASE_URL } from '@/util/constants';
import { hasDirtyFields, useHasDirtyFields } from '@/util/util';

import {
  MAX_TXT_PREVIEW_LENGTH,
  ROOT_DATASOURCE_NAMES,
  ROOT_DATASOURCE_TYPES,
} from '../../constants';
import { getPureName } from '../../helper';
import { useAPIDatasourcesDocuments } from '../../hooks/useAPIDatasourcesDocuments';
import { useAPIDocuments } from '../../hooks/useAPIDocuments';
import { useTabCategory } from '../../hooks/useCategory';
import { computeTxtPreview } from '../../utils/preview';
import { addTxtDocumentSchema, checkDocumentNameAvailability } from '../../validator';

type FormValues = yup.InferType<ReturnType<typeof addTxtDocumentSchema>>;

export const AddTxtDocument = () => {
  const dispatch = useDispatch();
  // Redux selectors
  const { datasourceId: datasourceIdParam } = useParams();
  const datasourceIdFromRedux = useSelector(selectDatasourceId);
  const documentId = useSelector(selectDocumentId);
  const accountId = useSelector(selectAccountId);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );
  const datasourceId = datasourceIdParam || datasourceIdFromRedux;

  // Hooks
  const { editorKey, forceRerender } = useForceRerender();
  const { collectionId } = useParams();
  const { t } = useTranslation();
  const { category } = useTabCategory();
  const { getDatasourceDocumentById } = useAPIDatasourcesDocuments({
    collection_id: collectionId,
    datasourceTab: category,
  });
  const { updateDocument, createDocument, getDocumentById, getStatus } = useAPIDocuments({
    collection_id: collectionId,
    datasource_id: datasourceId,
    document_id: documentId,
  });

  const documentFromInsideFolder = getDocumentById(documentId);
  const documentFromOutsideFolder = getDatasourceDocumentById(documentId);
  const document = documentFromInsideFolder || documentFromOutsideFolder;

  // Use the new custom hook for document content
  const { content: documentContent, isSuccess: isContentLoaded } = useDocumentContent({
    collectionId,
    datasourceId,
    documentId,
    enabled: !!datasourceId && !!documentId && !!collectionId,
  });

  const { uploadFileToUrl } = useFaqSubmit();
  const { datasources, createDatasourceAsync } = useDatasources(collectionId, datasourceId);

  // Local state
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);

  // Local variables
  const isEditing = !!datasourceId && !!documentId;
  const isFormLoading = isEditing && getStatus === 'pending';

  const showKeepPublished = isEditing && !document?.is_draft && document?.status === 'available';

  // RHF
  const form = useForm<FormValues>({
    resolver: yupResolver<FormValues, unknown, FormValues>(
      addTxtDocumentSchema(
        collectionId || '',
        datasourceId || '',
        documentId || undefined,
        getPureName(document?.name)
      )
    ),
    defaultValues: { title: '', content: '', externalUrl: '' },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  /**
   * Uploads a file and creates or updates a document in the specified datasource.
   * @param datasourceId - The datasource ID to upload to
   * @param data - The form values
   * @param isEditing - Whether this is an edit operation
   * @returns Promise<boolean> - Returns true if successful, false otherwise
   */
  async function saveTxtToDatasource(
    datasourceId: string,
    data: FormValues,
    isEditing?: boolean
  ): Promise<boolean> {
    const originalName = getPureName(document?.name);
    const newName = data.title.trim();
    const nameHasChanged = !originalName || newName.toLowerCase() !== originalName.toLowerCase();

    // Only check for duplicate name when creating new or when name has changed during edit
    if (!isEditing || nameHasChanged) {
      const nameCheckResult = await checkDocumentNameAvailability({
        collectionId,
        datasourceId,
        name: data.title,
        documentId: isEditing ? documentId : undefined,
      });

      if (!nameCheckResult.available) {
        form.setError('title', {
          type: 'manual',
          message: nameCheckResult.message || 'This name is already taken',
        });
        return false;
      }
    }

    const file = new File([data.content], data.title, { type: 'text/plain' });
    const preview = computeTxtPreview(data.content, MAX_TXT_PREVIEW_LENGTH);
    let url: string | undefined;
    try {
      const resp = await uploadFileToUrl(
        file,
        `${FILES_BASE_URL}/${accountId}/${collectionId}/${datasourceId}`
      );
      url = resp?.url;
      if (isEditing) {
        // null, not a fallback to the saved value: an emptied field has to clear the source.
        const externalUrl = data.externalUrl || null;
        updateDocument({
          document_id: documentId,
          datasource_id: datasourceId,
          name: data.title,
          type: 'txt',
          file_url: url,
          is_draft: !data.keep_published,
          external_url: externalUrl,
          metadata: { ...document?.metadata, preview },
        });

        dispatch(setSidebarContent(null));
      } else {
        createDocument({
          datasource_id: datasourceId,
          type: 'txt',
          name: data.title,
          file_url: url,
          external_url: data.externalUrl || undefined,
          metadata: { preview },
        });
      }
      return true;
    } catch (error) {
      console.error('Failed to upload file:', error);
      return false;
    }
  }

  /**
   * Handles editing an existing TXT document.
   */
  async function handleEditTxt(datasourceId: string, data: FormValues) {
    const success = await saveTxtToDatasource(datasourceId, data, true);

    if (success) {
      // Reset form to prepare for next TXT creation
      form.reset({ title: '', content: '', externalUrl: '' });
      forceRerender();
      dispatch(setSidebarContent('add_txt_document'));
    }
  }

  // Refactored onSubmit logic
  const onSubmit = async (data: FormValues) => {
    if (isEditing) {
      await handleEditTxt(datasourceId, data);
      return;
    }

    const file = new File([data.content], data.title, { type: 'text/plain' });
    const rootDocumentsDatasource = datasources?.find(
      (datasource) =>
        datasource.type === DatasourceType.FILES &&
        datasource.name === ROOT_DATASOURCE_NAMES.documents
    );

    const filesDatasourceExists = !!rootDocumentsDatasource;
    const isInsideFolder = !!datasourceIdParam;
    let targetDatasourceId: string;

    if (isInsideFolder) {
      targetDatasourceId = datasourceId;
    } else if (filesDatasourceExists) {
      targetDatasourceId = rootDocumentsDatasource.datasource_id;
    } else {
      // Create new files datasource
      const { datasource_id } = await createDatasourceAsync(
        ROOT_DATASOURCE_TYPES[DatasourceType.FILES]
      );
      targetDatasourceId = datasource_id;

      // Check for duplicate name before uploading
      const nameCheckResult = await checkDocumentNameAvailability({
        collectionId,
        datasourceId: targetDatasourceId,
        name: data.title,
      });

      if (!nameCheckResult.available) {
        form.setError('title', {
          type: 'manual',
          message: nameCheckResult.message || 'This name is already taken',
        });
        return;
      }

      // Upload file and create document
      try {
        const response = await uploadFileToUrl(
          file,
          `${FILES_BASE_URL}/${accountId}/${collectionId}/${datasource_id}`
        );
        createDocument({
          datasource_id: targetDatasourceId,
          type: 'txt',
          name: data.title,
          file_url: response?.url,
          external_url: data.externalUrl || undefined,
          metadata: { preview: computeTxtPreview(data.content, MAX_TXT_PREVIEW_LENGTH) },
        });
      } catch (error) {
        console.error('Failed to upload file:', error);
        return;
      }
      // Reset form and sidebar
      form.reset({ title: '', content: '', externalUrl: '' });
      forceRerender();
      dispatch(setSidebarContent('add_txt_document'));
      return;
    }

    // Save TXT to the determined datasource
    const success = await saveTxtToDatasource(targetDatasourceId, data);

    if (success) {
      // Reset form and sidebar
      form.reset({ title: '', content: '', externalUrl: '' });
      forceRerender();
      dispatch(setSidebarContent('add_txt_document'));
    }
  };

  useEffect(() => {
    if (!documentId || !datasourceId || !collectionId) {
      // Reset form values if datasourceId or documentId is null
      // This is needed to avoid the form values being set to the previous files
      // when the user clicks on the "Add" button
      form.reset({
        title: '',
        content: '',
        externalUrl: '',
        ...(isEditing && { keep_published: true }),
      });
      forceRerender();
      return;
    }

    // Load document content when available
    if (getStatus === 'success' && isContentLoaded && documentContent) {
      // Reset the entire form with the loaded data as default values
      // This prevents the form from being marked as dirty
      form.reset({
        title: getPureName(document?.name) || '',
        content: documentContent,
        externalUrl: document?.external_url || '',
        ...(isEditing && { keep_published: !document?.is_draft }),
      });

      // Mark data as loaded after resetting the form
      forceRerender();
    }
  }, [
    collectionId,
    datasourceId,
    documentId,
    form,
    isEditing,
    document,
    getStatus,
    isContentLoaded,
    documentContent,
    forceRerender,
  ]);

  // Handlers
  const resetReduxState = () => {
    dispatch(setDocumentId(null));
    dispatch(setDatasourceId(null));
  };

  const handleClose = () => {
    const hasUnsavedChanges = hasDirtyFields(form);

    if (hasUnsavedChanges) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      dispatch(setSidebarContent(null));
      resetReduxState();
    }
  };

  const isDirty = useHasDirtyFields(form.control);
  useTrackFormState({ isDirty, formId: 'add-txt-document' });

  // Close the sidebar when the Escape key is pressed
  useEscapeKey(handleClose);

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2 className="text-base leading-5 font-medium">
          {isEditing
            ? t('collections.add_txt_document.edit_document')
            : t('collections.add_txt_document.add_document')}
        </h2>

        <Button variant="ghost-neutral" size="icon-sm" type="button" onClick={handleClose}>
          <XIcon size={16} className="text-muted-foreground" />
        </Button>
      </SidebarWrapperTitle>

      <Form {...form}>
        <SidebarWrapperForm onSubmit={form.handleSubmit(onSubmit)}>
          <SidebarWrapperContent className="px-1">
            <fieldset className="min-w-0 space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">{t('common.title')}</FormLabel>
                    <FormControl>
                      <Textarea
                        autoFocus={canWrite}
                        {...field}
                        readOnly={!canWrite}
                        onChange={(e) => {
                          field.onChange(e);
                          form.trigger('title');
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormDescription>
                      {t('collections.add_txt_document.title_helper')}
                    </FormDescription>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('collections.add_txt_document.content')}</FormLabel>
                    <FormControl>
                      {isFormLoading ? (
                        <div className="flex items-center gap-1 border-b p-1">
                          <Skeleton className="h-8 w-8" />
                          <Skeleton className="h-8 w-8" />
                          <Skeleton className="h-8 w-8" />
                          <Skeleton className="h-8 w-8" />
                          <Skeleton className="h-8 w-8" />
                          <Skeleton className="h-8 w-8" />
                        </div>
                      ) : (
                        // Fieldset is used to disable the editor toolbar
                        <fieldset disabled={!canWrite} className="min-w-0">
                          <MarkdownEditorBlockNote
                            name="content"
                            setValue={form.setValue}
                            defaultValue={field.value}
                            error={!!form.formState.errors.content}
                            value={field.value}
                            key={editorKey}
                            readOnly={!canWrite}
                          />
                        </fieldset>
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="externalUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      {t('collections.documents.edit_document.external_url_optional')}
                    </FormLabel>
                    <FormControl>
                      <URLInput
                        value={field.value}
                        onChange={field.onChange}
                        readOnly={!canWrite}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormDescription>
                      {t('collections.documents.edit_document.external_url_optional_description')}{' '}
                      <Link
                        to={COLLECTIONS_WEBPAGE_SOURCE_DOCS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-primary"
                      >
                        {t('common.learn_more')}
                      </Link>
                    </FormDescription>
                  </FormItem>
                )}
              />

              {showKeepPublished && (
                <FormField
                  control={form.control}
                  name="keep_published"
                  render={({ field }) => (
                    <FormItem className="flex gap-2 space-y-0 rounded-md border p-4">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={!canWrite}
                        />
                      </FormControl>

                      <FormLabel className="flex flex-col items-start gap-1 leading-none">
                        {t('collections.add_faq.keep_published')}

                        <FormDescription>
                          {t('collections.add_faq.keep_published_description')}
                        </FormDescription>
                      </FormLabel>
                    </FormItem>
                  )}
                />
              )}
            </fieldset>
          </SidebarWrapperContent>

          {canWrite && (
            <SidebarWrapperFooter>
              <Button aria-label="submit-txt-document-form" type="submit" disabled={isFormLoading}>
                {isEditing ? t('common.update') : t('common.create')}
              </Button>
              <Button type="button" variant="ghost-neutral" onClick={handleClose}>
                {t('common.cancel')}
              </Button>
            </SidebarWrapperFooter>
          )}
        </SidebarWrapperForm>
      </Form>

      {isUnsavedChangesModalOpen && (
        <UnsavedChangesModal
          onDiscard={() => {
            dispatch(setSidebarContent(null));
            resetReduxState();
          }}
          onGoBack={() => {
            setIsUnsavedChangesModalOpen(false);
          }}
          onOpenChange={setIsUnsavedChangesModalOpen}
        />
      )}
    </SidebarWrapper>
  );
};
